import type { DrawerCreatedByFilter, DrawerPageScope, DrawerSortOrder, DrawerStatusFilter, ProjectMember, TestingItem, TestingItemPriority, TestingItemSeverity, TestingItemStatus } from '../types'
import { DRAWER_CSS } from './styles'

type DrawerGroupBy = 'none' | 'status' | 'priority' | 'severity' | 'assignee' | 'page'

interface DocumentPictureInPicture {
  window: Window | null
  requestWindow(options?: { width?: number; height?: number; disallowReturnToOpener?: boolean; preferInitialWindowPlacement?: boolean }): Promise<Window>
  addEventListener(type: 'enter', listener: (event: Event & { window: Window }) => void): void
  removeEventListener(type: 'enter', listener: (event: Event & { window: Window }) => void): void
}

declare global {
  interface Window {
    documentPictureInPicture?: DocumentPictureInPicture
  }
}

interface DrawerCallbacks {
  onOpenItem: (itemId: string | null) => void
  onChangeAssigneeFilter: (value: 'me' | 'all' | 'unassigned' | number) => void
  onChangePageScope: (value: DrawerPageScope) => void
  onChangeStatusFilter: (value: DrawerStatusFilter) => void
  onChangeCreatedByFilter: (value: DrawerCreatedByFilter) => void
  onChangeSortOrder: (value: DrawerSortOrder) => void

  onUpdateItem: (itemId: string, patch: {
    title?: string
    description?: string | null
    priority?: TestingItemPriority
    severity?: TestingItemSeverity
    status?: TestingItemStatus
    assignedTo?: number | null
    prLink?: string | null
  }) => void | Promise<void>
  onAddComment: (itemId: string, message: string) => void | Promise<void>
  onEditComment: (itemId: string, commentId: string, message: string) => void | Promise<void>
  onResolveItem: (itemId: string, resolution: string) => void | Promise<void>
  onCopy: (itemId?: string) => boolean | Promise<boolean>
  onClose: () => void
  onPipClosed: () => void
}

const STATUS_LABELS: Record<TestingItemStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
  reopened: 'Reopened',
}

const PRIORITY_LABELS: Record<TestingItemPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

const SEVERITY_LABELS: Record<TestingItemSeverity, string> = {
  minor: 'Minor',
  major: 'Major',
  critical: 'Critical',
  blocker: 'Blocker',
}

function esc(value: string | undefined | null): string {
  return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function pagePath(url: string): string {
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

function personName(member: ProjectMember | null): string {
  if (!member) return 'Unassigned'
  if (member.name) return member.name
  return [member.firstname, member.lastname].filter(Boolean).join(' ') || `Staff #${member.staffid}`
}

function screenshotUrl(screenshot: string | undefined, endpoint: string): string | null {
  if (!screenshot) return null
  if (screenshot.startsWith('data:')) return screenshot
  return `${endpoint.replace(/\/$/, '')}/${screenshot.replace(/^\//, '')}`
}

function detailPlaceholder(item: TestingItem, message: string): string {
  return item.hasFullDetails === false ? message : ''
}

function itemStatusLabel(status: TestingItemStatus): string {
  return STATUS_LABELS[status]
}

function itemPriorityLabel(priority: TestingItemPriority): string {
  return PRIORITY_LABELS[priority]
}

function itemSeverityLabel(severity: TestingItemSeverity): string {
  return SEVERITY_LABELS[severity]
}

function commentAvatar(name: string): string {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('')
  return initials || '?'
}

function formatCommentTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function formatDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date)
}

function relativeAge(value: string): string {
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return ''
  const mins = Math.floor((Date.now() - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

function creatorName(item: TestingItem, members: ProjectMember[]): string {
  if (item.createdName) return item.createdName
  const full = [item.createdFirst, item.createdLast].filter(Boolean).join(' ')
  if (full) return full
  const member = members.find(entry => entry.staffid === item.createdBy) ?? null
  return member ? personName(member) : 'Unknown'
}

export class IssueDrawer {
  private static readonly VIEWPORT_MARGIN = 16
  private static readonly SCREENSHOT_ZOOM_MIN = 1
  private static readonly SCREENSHOT_ZOOM_MAX = 3
  private static readonly SCREENSHOT_ZOOM_STEP = 0.25
  private host: HTMLElement | null = null
  private shadow: ShadowRoot | null = null
  private items: TestingItem[] = []
  private members: ProjectMember[] = []
  private selectedItemId: string | null = null
  private endpoint = '/visual-qa'
  private renderKey = ''
  private assigneeFilter: 'me' | 'all' | 'unassigned' | number = 'me'
  private pageScope: DrawerPageScope = 'all'
  private statusFilter: DrawerStatusFilter = 'open'
  private createdByFilter: DrawerCreatedByFilter = 'all'
  private sortOrder: DrawerSortOrder = 'newest'
  private searchQuery = ''
  private groupBy: DrawerGroupBy = 'none'

  private currentUserName = 'Me'
  private listLoading = false
  private detailLoadingItemId: string | null = null
  private detailErrorItemId: string | null = null
  private dragging = false
  private dragOffset = { x: 0, y: 0 }
  private screenshotZoom = 1
  private zoomedItemId: string | null = null
  private screenshotExpanded = false
  private editingCommentId: string | null = null
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null
  private mode: 'overlay' | 'pip' = 'overlay'
  private pipWindow: Window | null = null
  private readonly boundPipPageHide = (): void => {
    this.pipWindow = null
    this.mode = 'overlay'
    this.teardownDrag()
    this.host?.remove()
    this.host = null
    this.shadow = null
    this.callbacks.onPipClosed()
  }
  private readonly boundResize = (): void => this.clampIntoViewport()
  private readonly boundViewportChange = (): void => this.clampIntoViewport()

  private boundDragStart = (evt: Event): void => {
    if (!this.host) return
    const event = evt as MouseEvent
    const target = event.target as Element | null
    if (!target?.closest('.header') || target.closest('button, input, textarea, select')) return
    this.dragging = true
    const rect = this.host.getBoundingClientRect()
    this.dragOffset = { x: event.clientX - rect.left, y: event.clientY - rect.top }
    event.preventDefault()
  }

  private boundDragMove = (event: MouseEvent): void => {
    if (!this.host || !this.dragging) return
    const viewport = this.viewportBounds()
    const nextLeft = viewport.left + this.clampAxis(event.clientX - this.dragOffset.x - viewport.left, this.host.offsetWidth, viewport.width)
    const nextTop = viewport.top + this.clampAxis(event.clientY - this.dragOffset.y - viewport.top, this.host.offsetHeight, viewport.height)
    Object.assign(this.host.style, {
      left: `${nextLeft}px`,
      top: `${nextTop}px`,
      right: 'auto',
      bottom: 'auto',
    })
  }

  private boundDragEnd = (): void => {
    this.dragging = false
  }

  constructor(private readonly callbacks: DrawerCallbacks) {}

  isOpen(): boolean {
    return this.host !== null
  }

  show(items: TestingItem[], members: ProjectMember[], selectedItemId: string | null, endpoint: string, assigneeFilter: 'me' | 'all' | 'unassigned' | number, pageScope: DrawerPageScope, statusFilter: DrawerStatusFilter, createdByFilter: DrawerCreatedByFilter, sortOrder: DrawerSortOrder, currentUserName: string, listLoading: boolean, detailLoadingItemId: string | null, detailErrorItemId: string | null): void {
    this.items = items
    this.members = members
    this.syncScreenshotZoom(selectedItemId)
    this.selectedItemId = selectedItemId
    this.endpoint = endpoint
    this.assigneeFilter = assigneeFilter
    this.pageScope = pageScope
    this.statusFilter = statusFilter
    this.createdByFilter = createdByFilter
    this.sortOrder = sortOrder
    this.currentUserName = currentUserName
    this.listLoading = listLoading
    this.detailLoadingItemId = detailLoadingItemId
    this.detailErrorItemId = detailErrorItemId
    if (!this.host) this.build()
    this.render()
  }

  update(items: TestingItem[], members: ProjectMember[], selectedItemId: string | null, endpoint: string, assigneeFilter: 'me' | 'all' | 'unassigned' | number, pageScope: DrawerPageScope, statusFilter: DrawerStatusFilter, createdByFilter: DrawerCreatedByFilter, sortOrder: DrawerSortOrder, currentUserName: string, listLoading: boolean, detailLoadingItemId: string | null, detailErrorItemId: string | null): void {
    this.items = items
    this.members = members
    this.syncScreenshotZoom(selectedItemId)
    this.selectedItemId = selectedItemId
    this.endpoint = endpoint
    this.assigneeFilter = assigneeFilter
    this.pageScope = pageScope
    this.statusFilter = statusFilter
    this.createdByFilter = createdByFilter
    this.sortOrder = sortOrder
    this.currentUserName = currentUserName
    this.listLoading = listLoading
    this.detailLoadingItemId = detailLoadingItemId
    this.detailErrorItemId = detailErrorItemId
    const nextKey = this.keyFor(items, members, selectedItemId)
    if (this.host && nextKey === this.renderKey) return
    if (this.host) this.render()
  }

  close(): void {
    this.teardownDrag()
    if (this.pipWindow) {
      this.pipWindow.removeEventListener('pagehide', this.boundPipPageHide)
      this.pipWindow.close()
      this.pipWindow = null
    }
    this.mode = 'overlay'
    this.host?.remove()
    this.host = null
    this.shadow = null
    this.selectedItemId = null
    this.assigneeFilter = 'me'
    this.pageScope = 'all'
    this.statusFilter = 'open'
    this.createdByFilter = 'all'
    this.listLoading = false
    this.detailLoadingItemId = null
    this.detailErrorItemId = null
    this.screenshotExpanded = false
    this.editingCommentId = null
    this.searchQuery = ''
    this.groupBy = 'none'
    this.syncScreenshotZoom(null)
    if (this.copyResetTimer !== null) clearTimeout(this.copyResetTimer)
    this.callbacks.onClose()
  }

  destroy(): void {
    this.teardownDrag()
    if (this.pipWindow) {
      this.pipWindow.removeEventListener('pagehide', this.boundPipPageHide)
      this.pipWindow.close()
      this.pipWindow = null
    }
    this.mode = 'overlay'
    this.host?.remove()
    this.host = null
    this.shadow = null
    this.assigneeFilter = 'me'
    this.pageScope = 'all'
    this.statusFilter = 'open'
    this.createdByFilter = 'all'
    this.listLoading = false
    this.detailLoadingItemId = null
    this.detailErrorItemId = null
    this.screenshotExpanded = false
    this.editingCommentId = null
    this.searchQuery = ''
    this.groupBy = 'none'
    this.syncScreenshotZoom(null)
    if (this.copyResetTimer !== null) clearTimeout(this.copyResetTimer)
  }

  private showCopyFeedback(button: HTMLButtonElement, copied: boolean, originalLabel: string): void {
    if (this.copyResetTimer !== null) clearTimeout(this.copyResetTimer)
    button.textContent = copied ? 'Copied' : 'Copy failed'
    button.setAttribute('aria-label', copied ? 'Copied' : 'Copy failed')
    this.copyResetTimer = setTimeout(() => {
      button.textContent = originalLabel
      button.setAttribute('aria-label', originalLabel)
      this.copyResetTimer = null
    }, 1200)
  }

  private teardownDrag(): void {
    this.shadow?.removeEventListener('mousedown', this.boundDragStart)
    document.removeEventListener('mousemove', this.boundDragMove)
    document.removeEventListener('mouseup', this.boundDragEnd)
    window.removeEventListener('resize', this.boundResize)
    window.visualViewport?.removeEventListener('resize', this.boundViewportChange)
    window.visualViewport?.removeEventListener('scroll', this.boundViewportChange)
    this.dragging = false
  }

  private clamp(value: number, min: number, max: number): number {
    if (max < min) return min
    return Math.min(Math.max(value, min), max)
  }

  private viewportBounds(): { left: number; top: number; width: number; height: number } {
    const viewport = window.visualViewport
    return viewport
      ? { left: viewport.offsetLeft, top: viewport.offsetTop, width: viewport.width, height: viewport.height }
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
  }

  private clampAxis(offset: number, size: number, viewportSize: number): number {
    const max = Math.max(0, viewportSize - size - IssueDrawer.VIEWPORT_MARGIN)
    const min = Math.min(IssueDrawer.VIEWPORT_MARGIN, max)
    return this.clamp(offset, min, max)
  }

  private clampIntoViewport(): void {
    if (!this.host) return
    const viewport = this.viewportBounds()
    const rect = this.host.getBoundingClientRect()
    Object.assign(this.host.style, {
      left: `${viewport.left + this.clampAxis(rect.left - viewport.left, rect.width, viewport.width)}px`,
      top: `${viewport.top + this.clampAxis(rect.top - viewport.top, rect.height, viewport.height)}px`,
      right: 'auto',
      bottom: 'auto',
    })
  }

  private clampScreenshotZoom(value: number): number {
    return this.clamp(value, IssueDrawer.SCREENSHOT_ZOOM_MIN, IssueDrawer.SCREENSHOT_ZOOM_MAX)
  }

  private syncScreenshotZoom(selectedItemId: string | null): void {
    if (selectedItemId === this.zoomedItemId) return
    this.zoomedItemId = selectedItemId
    this.screenshotZoom = IssueDrawer.SCREENSHOT_ZOOM_MIN
    this.screenshotExpanded = false
  }

  private setScreenshotZoom(value: number): void {
    this.screenshotZoom = this.clampScreenshotZoom(value)
    this.render()
  }

  private build(): void {
    const targetDoc = this.mode === 'pip' && this.pipWindow ? this.pipWindow.document : document
    this.host = targetDoc.createElement('div')
    this.host.setAttribute('data-visual-qa', 'issue-drawer')
    if (this.mode === 'pip') this.host.setAttribute('data-pip', '')
    if (this.mode === 'overlay') {
      Object.assign(this.host.style, {
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: '2147483646',
      })
    }
    this.shadow = this.host.attachShadow({ mode: 'open' })

    if (this.mode === 'pip') {
      const styleEl = targetDoc.createElement('style')
      styleEl.textContent = DRAWER_CSS
      this.shadow.appendChild(styleEl)
    } else {
      const sheet = new CSSStyleSheet()
      sheet.replaceSync(DRAWER_CSS)
      this.shadow.adoptedStyleSheets = [sheet]
    }

    if (this.mode === 'overlay') {
      this.shadow.addEventListener('mousedown', this.boundDragStart)
      document.addEventListener('mousemove', this.boundDragMove)
      document.addEventListener('mouseup', this.boundDragEnd)
      window.addEventListener('resize', this.boundResize)
      window.visualViewport?.addEventListener('resize', this.boundViewportChange)
      window.visualViewport?.addEventListener('scroll', this.boundViewportChange)
    }

    const root = this.mode === 'pip' && this.pipWindow
      ? this.pipWindow.document.body
      : (document.getElementById('visual-qa-root') ?? document.body)
    root.appendChild(this.host)
  }

  async openPip(): Promise<boolean> {
    const pip = window.documentPictureInPicture
    if (!pip) return false
    if (pip.window) return false
    const pipWindow = await pip.requestWindow({
      width: 1100,
      height: 720,
    })
    pipWindow.addEventListener('pagehide', this.boundPipPageHide)
    this.pipWindow = pipWindow
    this.mode = 'pip'
    this.host?.remove()
    this.host = null
    this.shadow = null
    this.build()
    this.render()
    return true
  }

  isPipActive(): boolean {
    return this.mode === 'pip' && this.pipWindow !== null
  }

  private render(): void {
    if (!this.shadow) return
    this.renderKey = this.keyFor(this.items, this.members, this.selectedItemId)
    const targetDoc = this.mode === 'pip' && this.pipWindow ? this.pipWindow.document : document
    if (this.mode === 'pip') {
      while (this.shadow.childNodes.length > 1) {
        this.shadow.removeChild(this.shadow.lastChild!)
      }
    } else {
      this.shadow.replaceChildren()
    }

    const selectedItem = this.selectedItemId ? this.items.find(item => item.id === this.selectedItemId) ?? null : null
    const tableMode = !selectedItem && this.mode === 'pip'
    const drawer = targetDoc.createElement('div')
    drawer.className = selectedItem ? 'drawer drawer-detail' : tableMode ? 'drawer drawer-table' : 'drawer drawer-list'
    drawer.innerHTML = this.html()

    drawer.querySelector('.close-btn')?.addEventListener('click', () => this.close())
    drawer.querySelector<HTMLButtonElement>('[data-action="copy"]')?.addEventListener('click', async event => {
      const button = event.currentTarget as HTMLButtonElement
      const copied = await this.callbacks.onCopy(this.selectedItemId ?? undefined)
      this.showCopyFeedback(button, copied, selectedItem ? 'Copy item' : 'Copy list')
    })
    drawer.querySelector('[data-action="back"]')?.addEventListener('click', () => {
      this.editingCommentId = null
      this.callbacks.onOpenItem(null)
    })
    this.attachFilterHandlers(drawer)

    if (tableMode) {
      this.attachTableHandlers(drawer)
    } else {
      drawer.querySelectorAll<HTMLElement>('[data-item-id]').forEach(row => {
        const itemId = row.dataset.itemId
        if (!itemId) return
        row.addEventListener('click', () => this.callbacks.onOpenItem(itemId))
        row.addEventListener('keydown', event => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          this.callbacks.onOpenItem(itemId)
        })
      })
      drawer.querySelectorAll<HTMLButtonElement>('[data-close-id]').forEach(button => {
        button.addEventListener('click', async event => {
          event.stopPropagation()
          const closeId = button.dataset.closeId
          if (!closeId) return
          const original = button.innerHTML
          button.innerHTML = `<span class="issue-row-close-spinner"></span>`
          button.disabled = true
          try {
            await this.callbacks.onUpdateItem(closeId, { status: 'closed' })
          } finally {
            button.innerHTML = original
            button.disabled = false
          }
        })
      })
    }

    if (selectedItem) this.attachDetailHandlers(drawer, selectedItem)

    this.shadow.appendChild(drawer)
    if (this.mode === 'overlay') this.clampIntoViewport()
  }

  private attachFilterHandlers(drawer: HTMLElement): void {
    const assigneeFilterSelect = drawer.querySelector<HTMLSelectElement>('[data-field="assignee-filter"]')
    if (assigneeFilterSelect) {
      assigneeFilterSelect.addEventListener('change', event => {
        const value = (event.currentTarget as HTMLSelectElement).value
        this.assigneeFilter = value === 'me' || value === 'all' || value === 'unassigned' ? value : Number(value)
        this.callbacks.onChangeAssigneeFilter(this.assigneeFilter)
        this.render()
      })
    }
    drawer.querySelector<HTMLSelectElement>('[data-field="page-scope"]')?.addEventListener('change', event => {
      const value = (event.currentTarget as HTMLSelectElement).value === 'page' ? 'page' : 'all'
      this.pageScope = value
      this.callbacks.onChangePageScope(value)
      this.render()
    })
    drawer.querySelector<HTMLSelectElement>('[data-field="status-filter"]')?.addEventListener('change', event => {
      const value = (event.currentTarget as HTMLSelectElement).value
      this.statusFilter = value === 'all' || value === 'open' || value === 'in_progress' || value === 'resolved' || value === 'closed' || value === 'reopened'
        ? value
        : 'open'
      this.callbacks.onChangeStatusFilter(this.statusFilter)
      this.render()
    })
    const createdByFilterSelect = drawer.querySelector<HTMLSelectElement>('[data-field="created-by-filter"]')
    if (createdByFilterSelect) {
      createdByFilterSelect.addEventListener('change', event => {
        const value = (event.currentTarget as HTMLSelectElement).value
        this.createdByFilter = value === 'me' || value === 'all' ? value : Number(value)
        this.callbacks.onChangeCreatedByFilter(this.createdByFilter)
        this.render()
      })
    }
    drawer.querySelector<HTMLSelectElement>('[data-field="sort-order"]')?.addEventListener('change', event => {
      const value = (event.currentTarget as HTMLSelectElement).value
      this.sortOrder = value === 'oldest' || value === 'recently_updated' || value === 'priority' ? value : 'newest'
      this.callbacks.onChangeSortOrder(this.sortOrder)
      this.render()
    })
    drawer.querySelector<HTMLSelectElement>('[data-field="group-by"]')?.addEventListener('change', event => {
      const value = (event.currentTarget as HTMLSelectElement).value
      this.groupBy = value === 'status' || value === 'priority' || value === 'severity' || value === 'assignee' || value === 'page'
        ? value
        : 'none'
      this.render()
    })
    const searchInput = drawer.querySelector<HTMLInputElement>('[data-field="search"]')
    searchInput?.addEventListener('input', event => {
      const input = event.currentTarget as HTMLInputElement
      const caret = input.selectionStart
      this.searchQuery = input.value
      this.render()
      const next = this.shadow?.querySelector<HTMLInputElement>('[data-field="search"]')
      if (next) {
        next.focus()
        if (caret != null) next.setSelectionRange(caret, caret)
      }
    })
  }

  private attachTableHandlers(drawer: HTMLElement): void {
    drawer.querySelectorAll<HTMLButtonElement>('[data-open-id]').forEach(button => {
      button.addEventListener('click', () => {
        const itemId = button.dataset.openId
        if (itemId) this.callbacks.onOpenItem(itemId)
      })
    })

    drawer.querySelectorAll<HTMLButtonElement>('[data-close-id]').forEach(button => {
      button.addEventListener('click', async () => {
        const closeId = button.dataset.closeId
        if (!closeId) return
        const original = button.innerHTML
        button.innerHTML = `<span class="issue-row-close-spinner"></span>`
        button.disabled = true
        try {
          await this.callbacks.onUpdateItem(closeId, { status: 'closed' })
        } finally {
          button.innerHTML = original
          button.disabled = false
        }
      })
    })

    drawer.querySelectorAll<HTMLSelectElement>('select[data-table-field][data-item-id]').forEach(select => {
      select.addEventListener('change', async () => {
        const itemId = select.dataset.itemId
        const field = select.dataset.tableField
        if (!itemId || !field) return
        const item = this.items.find(entry => entry.id === itemId)
        if (!item) return

        const patch: {
          status?: TestingItemStatus
          priority?: TestingItemPriority
          severity?: TestingItemSeverity
          assignedTo?: number | null
        } = {}

        if (field === 'status') {
          const next = select.value as TestingItemStatus
          if (next === item.status) return
          patch.status = next
        } else if (field === 'priority') {
          const next = select.value as TestingItemPriority
          if (next === item.priority) return
          patch.priority = next
        } else if (field === 'severity') {
          const next = select.value as TestingItemSeverity
          if (next === item.severity) return
          patch.severity = next
        } else if (field === 'assignee') {
          const next = select.value === '' ? null : Number(select.value)
          if (next === item.assignedTo) return
          patch.assignedTo = next
        } else {
          return
        }

        select.disabled = true
        select.classList.add('table-cell-saving')
        try {
          await this.callbacks.onUpdateItem(itemId, patch)
        } catch {
          this.render()
        } finally {
          select.disabled = false
          select.classList.remove('table-cell-saving')
        }
      })
    })

    const commitTextField = async (input: HTMLInputElement): Promise<void> => {
      const itemId = input.dataset.itemId
      const field = input.dataset.tableField
      if (!itemId || !field) return
      const item = this.items.find(entry => entry.id === itemId)
      if (!item) return

      if (field !== 'title') return

      const next = input.value.trim() || item.title
      if (next === item.title) {
        input.value = item.title
        return
      }
      input.disabled = true
      input.classList.add('table-cell-saving')
      try {
        await this.callbacks.onUpdateItem(itemId, { title: next })
      } catch {
        input.value = item.title
        this.render()
      } finally {
        input.disabled = false
        input.classList.remove('table-cell-saving')
      }
    }

    drawer.querySelectorAll<HTMLInputElement>('input[data-table-field][data-item-id]').forEach(input => {
      input.addEventListener('keydown', event => {
        if (event.key !== 'Enter') return
        event.preventDefault()
        input.blur()
      })
      input.addEventListener('blur', () => {
        void commitTextField(input)
      })
    })
  }

  private attachDetailHandlers(drawer: HTMLElement, item: TestingItem): void {
    const titleInput = drawer.querySelector<HTMLInputElement>('[data-field="title"]')
    const descriptionInput = drawer.querySelector<HTMLTextAreaElement>('[data-field="description"]')
    const prioritySelect = drawer.querySelector<HTMLSelectElement>('[data-field="priority"]')
    const severitySelect = drawer.querySelector<HTMLSelectElement>('[data-field="severity"]')
    const statusSelect = drawer.querySelector<HTMLSelectElement>('[data-field="status"]')
    const assigneeSelect = drawer.querySelector<HTMLSelectElement>('[data-field="assignee"]')
    const prLinkInput = drawer.querySelector<HTMLInputElement>('[data-field="pr-link"]')
    const saveButton = drawer.querySelector<HTMLButtonElement>('[data-action="save-item"]')
    const commentInput = drawer.querySelector<HTMLTextAreaElement>('[data-field="new-comment"]')
    const addCommentButton = drawer.querySelector<HTMLButtonElement>('[data-action="add-comment"]')
    const resolutionInput = drawer.querySelector<HTMLTextAreaElement>('[data-field="resolution"]')
    const resolveButton = drawer.querySelector<HTMLButtonElement>('[data-action="resolve"]')
    const zoomInButtons = drawer.querySelectorAll<HTMLButtonElement>('[data-action="zoom-in"]')
    const zoomOutButtons = drawer.querySelectorAll<HTMLButtonElement>('[data-action="zoom-out"]')
    const zoomResetButtons = drawer.querySelectorAll<HTMLButtonElement>('[data-action="zoom-reset"]')
    const openImageButton = drawer.querySelector<HTMLButtonElement>('[data-action="open-image"]')
    const closeImageButton = drawer.querySelector<HTMLButtonElement>('[data-action="close-image"]')
    const modalBackdrop = drawer.querySelector<HTMLElement>('[data-action="close-image-backdrop"]')

    saveButton?.addEventListener('click', async () => {
      const nextTitle = titleInput?.value.trim() || item.title
      const nextDescription = descriptionInput ? (descriptionInput.value.trim() || null) : undefined
      const nextPriority = prioritySelect?.value as TestingItemPriority
      const nextSeverity = severitySelect?.value as TestingItemSeverity
      const nextStatus = statusSelect?.value as TestingItemStatus
      const nextAssignedTo = assigneeSelect?.value === '' ? null : Number(assigneeSelect?.value)
      const nextPrLink = prLinkInput?.value.trim() || null

      const patch: {
        title?: string
        description?: string | null
        priority?: TestingItemPriority
        severity?: TestingItemSeverity
        status?: TestingItemStatus
        assignedTo?: number | null
        prLink?: string | null
      } = {}

      if (nextTitle !== item.title) patch.title = nextTitle
      if (nextDescription !== (item.description ?? null)) patch.description = nextDescription
      if (nextPriority !== item.priority) patch.priority = nextPriority
      if (nextSeverity !== item.severity) patch.severity = nextSeverity
      if (nextStatus !== item.status) patch.status = nextStatus
      if (nextAssignedTo !== item.assignedTo) patch.assignedTo = nextAssignedTo
      if (nextPrLink !== item.prLink) patch.prLink = nextPrLink

      if (Object.keys(patch).length === 0) return
      if (!saveButton) return

      const label = saveButton.querySelector<HTMLElement>('.btn-label')
      const originalText = label?.textContent ?? 'Save item'
      saveButton.classList.add('btn-loading')
      saveButton.disabled = true
      try {
        await this.callbacks.onUpdateItem(item.id, patch)
        saveButton.classList.remove('btn-loading')
        if (label) label.textContent = 'Saved ✓'
        setTimeout(() => {
          if (label) label.textContent = originalText
          saveButton.disabled = false
        }, 1500)
      } catch {
        if (label) label.textContent = originalText
        saveButton.classList.remove('btn-loading')
        saveButton.disabled = false
      }
    })

    addCommentButton?.addEventListener('click', async () => {
      const message = commentInput?.value.trim() ?? ''
      if (!message || !addCommentButton) return

      const label = addCommentButton.querySelector<HTMLElement>('.btn-label')
      const originalText = label?.textContent ?? 'Add comment'
      addCommentButton.classList.add('btn-loading')
      addCommentButton.disabled = true
      try {
        await this.callbacks.onAddComment(item.id, message)
        if (commentInput) commentInput.value = ''
      } finally {
        if (label) label.textContent = originalText
        addCommentButton.classList.remove('btn-loading')
        addCommentButton.disabled = false
      }
    })

    resolveButton?.addEventListener('click', async () => {
      const resolution = resolutionInput?.value.trim() ?? ''
      if (!resolution || !resolveButton) return

      const label = resolveButton.querySelector<HTMLElement>('.btn-label')
      const originalText = label?.textContent ?? 'Resolve item'
      resolveButton.classList.add('btn-loading')
      resolveButton.disabled = true
      try {
        await this.callbacks.onResolveItem(item.id, resolution)
      } finally {
        if (label) label.textContent = originalText
        resolveButton.classList.remove('btn-loading')
        resolveButton.disabled = false
      }
    })

    zoomInButtons.forEach(button => button.addEventListener('click', () => {
      this.setScreenshotZoom(this.screenshotZoom + IssueDrawer.SCREENSHOT_ZOOM_STEP)
    }))

    zoomOutButtons.forEach(button => button.addEventListener('click', () => {
      this.setScreenshotZoom(this.screenshotZoom - IssueDrawer.SCREENSHOT_ZOOM_STEP)
    }))

    zoomResetButtons.forEach(button => button.addEventListener('click', () => {
      this.setScreenshotZoom(IssueDrawer.SCREENSHOT_ZOOM_MIN)
    }))

    openImageButton?.addEventListener('click', () => {
      this.screenshotExpanded = true
      this.render()
    })

    closeImageButton?.addEventListener('click', () => {
      this.screenshotExpanded = false
      this.render()
    })

    modalBackdrop?.addEventListener('click', event => {
      if (event.target !== modalBackdrop) return
      this.screenshotExpanded = false
      this.render()
    })

    // Mouse wheel + Ctrl/Cmd zooms the screenshot viewport; plain scroll is left to the browser
    const screenshotViewport = drawer.querySelector<HTMLElement>('.screenshot-viewport')
    screenshotViewport?.addEventListener('wheel', event => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      const delta = event.deltaY < 0 ? IssueDrawer.SCREENSHOT_ZOOM_STEP : -IssueDrawer.SCREENSHOT_ZOOM_STEP
      this.setScreenshotZoom(this.screenshotZoom + delta)
    }, { passive: false })

    // Escape key navigates back to list when in detail view
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (this.screenshotExpanded) {
        event.stopPropagation()
        this.screenshotExpanded = false
        this.render()
      } else if (this.selectedItemId) {
        event.stopPropagation()
        this.editingCommentId = null
        this.callbacks.onOpenItem(null)
      }
    }
    this.shadow?.addEventListener('keydown', handleEscape as EventListener)

    // Inline comment editing — "Edit" button opens a textarea in-place
    drawer.querySelectorAll<HTMLButtonElement>('[data-comment-id]').forEach(button => {
      button.addEventListener('click', () => {
        const commentId = button.dataset.commentId
        if (!commentId) return
        this.editingCommentId = commentId
        this.render()
        requestAnimationFrame(() => {
          this.shadow?.querySelector<HTMLTextAreaElement>('[data-field="edit-comment"]')?.focus()
        })
      })
    })

    drawer.querySelector('[data-action="cancel-edit"]')?.addEventListener('click', () => {
      this.editingCommentId = null
      this.render()
    })

    const saveEditButton = drawer.querySelector<HTMLButtonElement>('[data-action="save-edit"]')
    saveEditButton?.addEventListener('click', () => {
      const commentId = saveEditButton.dataset.commentId
      if (!commentId) return
      const next = drawer.querySelector<HTMLTextAreaElement>('[data-field="edit-comment"]')?.value.trim()
      if (!next) return
      this.editingCommentId = null
      this.callbacks.onEditComment(item.id, commentId, next)
    })
  }

  private keyFor(items: TestingItem[], members: ProjectMember[], selectedItemId: string | null): string {
    return JSON.stringify({
      selectedItemId,
      editingCommentId: this.editingCommentId,
      listLoading: this.listLoading,
      detailLoadingItemId: this.detailLoadingItemId,
      detailErrorItemId: this.detailErrorItemId,
      assigneeFilter: this.assigneeFilter,
      pageScope: this.pageScope,
      statusFilter: this.statusFilter,
      createdByFilter: this.createdByFilter,
      sortOrder: this.sortOrder,
      searchQuery: this.searchQuery,
      groupBy: this.groupBy,
      mode: this.mode,
      currentUserName: this.currentUserName,
      members: members.map(member => [member.staffid, member.name, member.firstname, member.lastname]),
      items: items.map(item => [
        item.id,
        item.title,
        item.status,
        item.priority,
        item.severity,
        item.createdAt,
        item.updatedAt,
        item.comments.length,
        item.assignedTo,
        item.createdBy,
        item.createdName,
        item.prLink,
        item.url,
      ]),
    })
  }

  private assigneeFilterOptions(): string {
    const uniqueMembers = this.members
      .slice()
      .sort((a, b) => personName(a).localeCompare(personName(b)))
      .map(member => `<option value="${member.staffid}" ${this.assigneeFilter === member.staffid ? 'selected' : ''}>${esc(personName(member))}</option>`)
      .join('')
    return [`<option value="me" ${this.assigneeFilter === 'me' ? 'selected' : ''}>Assigned to ${esc(this.currentUserName)}</option>`, `<option value="all" ${this.assigneeFilter === 'all' ? 'selected' : ''}>All items</option>`, `<option value="unassigned" ${this.assigneeFilter === 'unassigned' ? 'selected' : ''}>Unassigned</option>`, uniqueMembers].join('')
  }

  private statusFilterOptions(): string {
    return [
      `<option value="open" ${this.statusFilter === 'open' ? 'selected' : ''}>Open</option>`,
      `<option value="in_progress" ${this.statusFilter === 'in_progress' ? 'selected' : ''}>In progress</option>`,
      `<option value="reopened" ${this.statusFilter === 'reopened' ? 'selected' : ''}>Reopened</option>`,
      `<option value="resolved" ${this.statusFilter === 'resolved' ? 'selected' : ''}>Resolved</option>`,
      `<option value="closed" ${this.statusFilter === 'closed' ? 'selected' : ''}>Closed</option>`,
      `<option value="all" ${this.statusFilter === 'all' ? 'selected' : ''}>All statuses</option>`,
    ].join('')
  }

  private createdByFilterOptions(): string {
    const uniqueMembers = this.members
      .slice()
      .sort((a, b) => personName(a).localeCompare(personName(b)))
      .map(member => `<option value="${member.staffid}" ${this.createdByFilter === member.staffid ? 'selected' : ''}>${esc(personName(member))}</option>`)
      .join('')
    return [
      `<option value="all" ${this.createdByFilter === 'all' ? 'selected' : ''}>All creators</option>`,
      `<option value="me" ${this.createdByFilter === 'me' ? 'selected' : ''}>Created by ${esc(this.currentUserName)}</option>`,
      uniqueMembers,
    ].join('')
  }

  private sortOptions(): string {
    return [
      `<option value="newest" ${this.sortOrder === 'newest' ? 'selected' : ''}>Newest first</option>`,
      `<option value="oldest" ${this.sortOrder === 'oldest' ? 'selected' : ''}>Oldest first</option>`,
      `<option value="recently_updated" ${this.sortOrder === 'recently_updated' ? 'selected' : ''}>Recently updated</option>`,
      `<option value="priority" ${this.sortOrder === 'priority' ? 'selected' : ''}>Priority</option>`,
    ].join('')
  }

  private pageScopeOptions(): string {
    return [
      `<option value="all" ${this.pageScope === 'all' ? 'selected' : ''}>All pages</option>`,
      `<option value="page" ${this.pageScope === 'page' ? 'selected' : ''}>Current page</option>`,
    ].join('')
  }

  private groupByOptions(): string {
    return [
      `<option value="none" ${this.groupBy === 'none' ? 'selected' : ''}>No grouping</option>`,
      `<option value="status" ${this.groupBy === 'status' ? 'selected' : ''}>Status</option>`,
      `<option value="priority" ${this.groupBy === 'priority' ? 'selected' : ''}>Priority</option>`,
      `<option value="severity" ${this.groupBy === 'severity' ? 'selected' : ''}>Severity</option>`,
      `<option value="assignee" ${this.groupBy === 'assignee' ? 'selected' : ''}>Assignee</option>`,
      `<option value="page" ${this.groupBy === 'page' ? 'selected' : ''}>Page</option>`,
    ].join('')
  }

  private statusCellOptions(selected: TestingItemStatus): string {
    return (Object.keys(STATUS_LABELS) as TestingItemStatus[])
      .map(status => `<option value="${status}" ${selected === status ? 'selected' : ''}>${esc(STATUS_LABELS[status])}</option>`)
      .join('')
  }

  private priorityCellOptions(selected: TestingItemPriority): string {
    return (Object.keys(PRIORITY_LABELS) as TestingItemPriority[])
      .map(priority => `<option value="${priority}" ${selected === priority ? 'selected' : ''}>${esc(PRIORITY_LABELS[priority])}</option>`)
      .join('')
  }

  private severityCellOptions(selected: TestingItemSeverity): string {
    return (Object.keys(SEVERITY_LABELS) as TestingItemSeverity[])
      .map(severity => `<option value="${severity}" ${selected === severity ? 'selected' : ''}>${esc(SEVERITY_LABELS[severity])}</option>`)
      .join('')
  }

  private assigneeCellOptions(selected: number | null): string {
    const members = this.members
      .slice()
      .sort((a, b) => personName(a).localeCompare(personName(b)))
      .map(member => `<option value="${member.staffid}" ${selected === member.staffid ? 'selected' : ''}>${esc(personName(member))}</option>`)
      .join('')
    return `<option value="" ${selected == null ? 'selected' : ''}>Unassigned</option>${members}`
  }

  private scopeSuffix(): string {
    return this.pageScope === 'page' ? 'on this page' : 'across all pages'
  }

  private listSubtitle(items: TestingItem[]): string {
    const count = items.length
    const suffix = this.scopeSuffix()
    const base = `${count} ${count === 1 ? 'item' : 'items'} ${suffix}`
    const tableNote = this.mode === 'pip' ? ' · table' : ''
    if (this.assigneeFilter === 'me') return `${base}${tableNote} · assigned to ${this.currentUserName}`
    if (this.assigneeFilter === 'unassigned') return `${base}${tableNote} · unassigned`
    const member = this.members.find(entry => entry.staffid === this.assigneeFilter) ?? null
    if (typeof this.assigneeFilter === 'number') return `${base}${tableNote} · assigned to ${personName(member)}`
    return `${base}${tableNote}`
  }

  private groupMeta(item: TestingItem): { key: string; label: string } {
    if (this.groupBy === 'status') return { key: item.status, label: itemStatusLabel(item.status) }
    if (this.groupBy === 'priority') return { key: item.priority, label: itemPriorityLabel(item.priority) }
    if (this.groupBy === 'severity') return { key: item.severity, label: itemSeverityLabel(item.severity) }
    if (this.groupBy === 'assignee') {
      const assignee = item.assignedName ?? personName(this.members.find(member => member.staffid === item.assignedTo) ?? null)
      return { key: item.assignedTo == null ? 'unassigned' : String(item.assignedTo), label: assignee }
    }
    if (this.groupBy === 'page') {
      const path = pagePath(item.url)
      return { key: path, label: path }
    }
    return { key: 'all', label: '' }
  }

  private groupedItems(items: TestingItem[]): Array<{ key: string; label: string; items: TestingItem[] }> {
    if (this.groupBy === 'none') return [{ key: 'all', label: '', items }]
    const groups = new Map<string, { key: string; label: string; items: TestingItem[] }>()
    for (const item of items) {
      const meta = this.groupMeta(item)
      const existing = groups.get(meta.key)
      if (existing) existing.items.push(item)
      else groups.set(meta.key, { key: meta.key, label: meta.label, items: [item] })
    }
    return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label))
  }

  private listEmptyHtml(): string {
    const icon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>`
    const msg = this.pageScope === 'page' ? 'No items on this page' : 'No items match the current filters'
    return `<div class="empty"><div class="empty-icon">${icon}</div>${msg}</div>`
  }

  private loadingHtml(message: string): string {
    return `
      <div class="loading-state" role="status" aria-live="polite">
        <div class="loading-spinner" aria-hidden="true"></div>
        <div class="loading-copy">${esc(message)}</div>
      </div>
    `
  }

  private filteredItems(): TestingItem[] {
    const query = this.searchQuery.trim().toLowerCase()
    if (!query) return this.items
    return this.items.filter(item =>
      item.title.toLowerCase().includes(query) || pagePath(item.url).toLowerCase().includes(query))
  }

  private html(): string {
    const selectedItem = this.selectedItemId ? this.items.find(item => item.id === this.selectedItemId) ?? null : null
    const listItems = this.filteredItems()
    const subtitle = this.listSubtitle(listItems)
    const tableMode = !selectedItem && this.mode === 'pip'
    const body = selectedItem
      ? this.detailHtml(selectedItem)
      : tableMode
        ? this.tableHtml(listItems)
        : this.itemsHtml(listItems)
    return `
      <div class="header">
        <div>
          <div class="drag-dots" aria-hidden="true"><span></span><span></span><span></span></div>
          <div class="title">${selectedItem ? esc(selectedItem.title) : tableMode ? 'Items table' : 'Items'}</div>
          <div class="subtitle">${selectedItem ? `${esc(pagePath(selectedItem.url))} · ${esc(itemStatusLabel(selectedItem.status))}` : subtitle}</div>
        </div>
        <button class="close-btn" title="Close" aria-label="Close"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg></button>
      </div>
      <div class="actions">
        ${selectedItem ? `<button class="btn-secondary" data-action="back" aria-label="Back to ${this.mode === 'pip' ? 'table' : 'item list'}">← Back</button>` : '<span></span>'}
        <button class="btn-primary" data-action="copy">${selectedItem ? 'Copy item' : 'Copy list'}</button>
      </div>
      <div class="list">
        ${body}
      </div>
    `
  }

  private filterBarHtml(items: TestingItem[], options?: { includeGroupBy?: boolean }): string {
    const includeGroupBy = options?.includeGroupBy === true
    return `
      <div class="list-filter${includeGroupBy ? ' list-filter--table' : ''}">
        ${this.listLoading && items.length > 0 ? this.loadingHtml('Refreshing items…') : ''}
        <input class="text-input list-search" data-field="search" type="search" placeholder="Search title or page…" value="${esc(this.searchQuery)}" />
        <div class="list-filter-grid${includeGroupBy ? ' list-filter-grid--table' : ''}">
          <label class="field-block">
            <span class="field-label">Pages</span>
            <select data-field="page-scope">${this.pageScopeOptions()}</select>
          </label>
          <label class="field-block">
            <span class="field-label">Sort by</span>
            <select data-field="sort-order">${this.sortOptions()}</select>
          </label>
          <label class="field-block">
            <span class="field-label">Assignee</span>
            <select data-field="assignee-filter">${this.assigneeFilterOptions()}</select>
          </label>
          <label class="field-block">
            <span class="field-label">Status</span>
            <select data-field="status-filter">${this.statusFilterOptions()}</select>
          </label>
          <label class="field-block">
            <span class="field-label">Created by</span>
            <select data-field="created-by-filter">${this.createdByFilterOptions()}</select>
          </label>
          ${includeGroupBy ? `
          <label class="field-block">
            <span class="field-label">Group by</span>
            <select data-field="group-by">${this.groupByOptions()}</select>
          </label>
          ` : ''}
        </div>
      </div>
    `
  }

  private itemsHtml(items: TestingItem[]): string {
    if (this.listLoading && items.length === 0) return this.loadingHtml('Loading items…')
    const closeIcon = `<svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg>`
    const rows = items.map((item, index) => {
      const assignee = item.assignedName ?? personName(this.members.find(member => member.staffid === item.assignedTo) ?? null)
      const reporter = creatorName(item, this.members)
      return `
        <div class="issue-row" data-item-id="${esc(item.id)}" role="button" tabindex="0">
          ${item.status === 'closed' ? '' : `<button class="issue-row-close" data-close-id="${esc(item.id)}" title="Close item" aria-label="Close item">${closeIcon}</button>`}
          <div class="issue-row-main">
            <div class="issue-index">${index + 1}</div>
            <div class="issue-copy">
              <div class="issue-title">${esc(item.title)}</div>
              <div class="issue-path" title="${esc(pagePath(item.url))}">${esc(pagePath(item.url))}</div>
              <div class="issue-badges">
                <span class="issue-badge issue-badge--${item.status}" title="Status: ${esc(itemStatusLabel(item.status))}">${esc(itemStatusLabel(item.status))}</span>
                <span class="issue-badge issue-badge--${item.priority}" title="Priority: ${esc(itemPriorityLabel(item.priority))}">P: ${esc(itemPriorityLabel(item.priority))}</span>
                <span class="issue-badge issue-badge--${item.severity}" title="Severity: ${esc(itemSeverityLabel(item.severity))}">S: ${esc(itemSeverityLabel(item.severity))}</span>
              </div>
              <div class="issue-meta" title="Reported ${esc(formatCommentTime(item.createdAt))}">${esc(reporter)} · ${esc(formatDate(item.createdAt))} · ${esc(relativeAge(item.createdAt))}</div>
              <div class="issue-meta">${esc(assignee)} · ${item.comments.length} ${item.comments.length === 1 ? 'comment' : 'comments'}</div>
            </div>
          </div>
        </div>
      `
    }).join('')
    return `
      ${this.filterBarHtml(items)}
      ${rows || this.listEmptyHtml()}
    `
  }

  private tableRowHtml(item: TestingItem, index: number): string {
    const path = pagePath(item.url)
    const closeIcon = `<svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg>`
    return `
      <tr class="items-table-row" data-table-row="${esc(item.id)}">
        <td class="items-table-index">${index + 1}</td>
        <td class="items-table-title">
          <input class="table-input" data-table-field="title" data-item-id="${esc(item.id)}" value="${esc(item.title)}" title="${esc(item.title)}" />
        </td>
        <td class="items-table-page" title="${esc(path)}">${esc(path)}</td>
        <td>
          <select class="table-select table-select--status" data-table-field="status" data-item-id="${esc(item.id)}">${this.statusCellOptions(item.status)}</select>
        </td>
        <td>
          <select class="table-select" data-table-field="priority" data-item-id="${esc(item.id)}">${this.priorityCellOptions(item.priority)}</select>
        </td>
        <td>
          <select class="table-select" data-table-field="severity" data-item-id="${esc(item.id)}">${this.severityCellOptions(item.severity)}</select>
        </td>
        <td>
          <select class="table-select" data-table-field="assignee" data-item-id="${esc(item.id)}">${this.assigneeCellOptions(item.assignedTo)}</select>
        </td>
        <td class="items-table-updated" title="${esc(formatCommentTime(item.updatedAt))}">${esc(relativeAge(item.updatedAt) || formatDate(item.updatedAt))}</td>
        <td class="items-table-actions">
          <button class="table-action-btn" data-open-id="${esc(item.id)}" type="button" title="Open details">Open</button>
          ${item.status === 'closed' ? '' : `<button class="table-action-btn table-action-btn--danger" data-close-id="${esc(item.id)}" type="button" title="Close item" aria-label="Close item">${closeIcon}</button>`}
        </td>
      </tr>
    `
  }

  private tableHtml(items: TestingItem[]): string {
    if (this.listLoading && items.length === 0) return this.loadingHtml('Loading items…')
    if (items.length === 0) {
      return `
        ${this.filterBarHtml(items, { includeGroupBy: true })}
        ${this.listEmptyHtml()}
      `
    }

    const groups = this.groupedItems(items)
    let rowIndex = 0
    const body = groups.map(group => {
      const rows = group.items.map(item => this.tableRowHtml(item, rowIndex++)).join('')
      if (!group.label) return rows
      return `
        <tr class="items-table-group">
          <td colspan="9">
            <div class="items-table-group-label">
              <span>${esc(group.label)}</span>
              <span class="items-table-group-count">${group.items.length}</span>
            </div>
          </td>
        </tr>
        ${rows}
      `
    }).join('')

    return `
      ${this.filterBarHtml(items, { includeGroupBy: true })}
      <div class="items-table-wrap">
        <table class="items-table">
          <thead>
            <tr>
              <th class="items-table-index">#</th>
              <th>Title</th>
              <th>Page</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Severity</th>
              <th>Assignee</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    `
  }

  private detailHtml(item: TestingItem): string {
    const screenshot = screenshotUrl(item.screenshot, this.endpoint)
    const zoomPercent = Math.round(this.screenshotZoom * 100)
    const hasFullDetails = item.hasFullDetails !== false
    const detailLoading = this.detailLoadingItemId === item.id
    const detailError = this.detailErrorItemId === item.id
    const assignee = item.assignedName ?? personName(this.members.find(member => member.staffid === item.assignedTo) ?? null)
    const route = pagePath(item.url)
    const assigneeOptions = [`<option value="">Unassigned</option>`]
      .concat(this.members.map(member => `<option value="${member.staffid}" ${item.assignedTo === member.staffid ? 'selected' : ''}>${esc(personName(member))}</option>`))
      .join('')
    const noCommentsIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`
    const comments = item.comments.map(comment => {
      if (this.editingCommentId === comment.id) {
        return `
          <div class="annotation-row editing">
            <textarea class="detail-input" data-field="edit-comment" rows="3">${esc(comment.message)}</textarea>
            <div class="edit-comment-actions">
              <button class="btn-secondary" data-action="cancel-edit">Cancel</button>
              <button class="btn-primary" data-action="save-edit" data-comment-id="${esc(comment.id)}">Save</button>
            </div>
          </div>
        `
      }
      return `
        <div class="annotation-row">
          <div class="annotation-row-top">
            <span class="comment-avatar">${esc(commentAvatar(comment.name))}</span>
            <span class="comment">${esc(comment.message)}</span>
          </div>
          <div class="annotation-meta">
            <span>${esc(comment.name)}</span>
            <span>${esc(formatCommentTime(comment.createdAt))}</span>
            <button class="text-btn" data-comment-id="${esc(comment.id)}">Edit</button>
          </div>
        </div>
      `
    }).join('') || `<div class="empty"><div class="empty-icon">${noCommentsIcon}</div>No comments yet</div>`

    return `
      <section class="issue-detail${detailLoading ? ' loading' : ''}">
        <div class="issue-detail-body">
        ${detailError ? '<div class="detail-alert detail-alert-error" role="status" aria-live="polite">Couldn’t load full item details. Showing the saved list data.</div>' : ''}
        <div class="detail-hero">
          <div class="detail-kicker">Item details</div>
          <div class="detail-route" title="${esc(route)}">${esc(route)}</div>
          <div class="detail-badges">
            <span class="issue-badge issue-badge--${item.status}" title="Status: ${esc(itemStatusLabel(item.status))}">${esc(itemStatusLabel(item.status))}</span>
            <span class="issue-badge issue-badge--${item.priority}" title="Priority: ${esc(itemPriorityLabel(item.priority))}">P: ${esc(itemPriorityLabel(item.priority))}</span>
            <span class="issue-badge issue-badge--${item.severity}" title="Severity: ${esc(itemSeverityLabel(item.severity))}">S: ${esc(itemSeverityLabel(item.severity))}</span>
          </div>
          <div class="detail-support">${esc(assignee)} · ${item.comments.length} ${item.comments.length === 1 ? 'comment' : 'comments'}</div>
          <div class="detail-support" title="Created ${esc(formatCommentTime(item.createdAt))} · Updated ${esc(formatCommentTime(item.updatedAt))}">Reported by ${esc(creatorName(item, this.members))} · ${esc(formatDate(item.createdAt))} (${esc(relativeAge(item.createdAt))})</div>
        </div>
        <label class="field-block">
          <span class="field-label">Title</span>
          <input class="text-input" data-field="title" value="${esc(item.title)}" />
        </label>
        <label class="field-block">
          <span class="field-label">Description</span>
          <textarea class="detail-input" data-field="description" rows="4" placeholder="${esc(detailPlaceholder(item, 'Not available'))}">${esc(item.description ?? '')}</textarea>
        </label>
        <div class="field-grid">
          <label>
            <span class="field-label">Priority</span>
            <select data-field="priority">
              <option value="low" ${item.priority === 'low' ? 'selected' : ''}>Low</option>
              <option value="medium" ${item.priority === 'medium' ? 'selected' : ''}>Medium</option>
              <option value="high" ${item.priority === 'high' ? 'selected' : ''}>High</option>
              <option value="critical" ${item.priority === 'critical' ? 'selected' : ''}>Critical</option>
            </select>
          </label>
          <label>
            <span class="field-label">Severity</span>
            <select data-field="severity">
              <option value="minor" ${item.severity === 'minor' ? 'selected' : ''}>Minor</option>
              <option value="major" ${item.severity === 'major' ? 'selected' : ''}>Major</option>
              <option value="critical" ${item.severity === 'critical' ? 'selected' : ''}>Critical</option>
              <option value="blocker" ${item.severity === 'blocker' ? 'selected' : ''}>Blocker</option>
            </select>
          </label>
          <label>
            <span class="field-label">Status</span>
            <select data-field="status">
              <option value="open" ${item.status === 'open' ? 'selected' : ''}>Open</option>
              <option value="in_progress" ${item.status === 'in_progress' ? 'selected' : ''}>In progress</option>
              <option value="resolved" ${item.status === 'resolved' ? 'selected' : ''}>Resolved</option>
              <option value="closed" ${item.status === 'closed' ? 'selected' : ''}>Closed</option>
              <option value="reopened" ${item.status === 'reopened' ? 'selected' : ''}>Reopened</option>
            </select>
          </label>
          <label>
            <span class="field-label">Assignee</span>
            <select data-field="assignee">${assigneeOptions}</select>
          </label>
        </div>
        <label class="field-block">
          <span class="field-label">PR Link</span>
          <input class="text-input" data-field="pr-link" value="${esc(item.prLink ?? '')}" placeholder="https://github.com/org/repo/pull/123" />
        </label>
        <div class="detail-button-row"><button class="btn-primary" data-action="save-item"><span class="btn-label">Save item</span><span class="btn-spinner" aria-hidden="true"></span></button></div>
        ${screenshot ? `
        <div class="screenshot-preview">
            <div class="screenshot-toolbar">
              <span class="field-label">Captured image</span>
              <div class="screenshot-controls">
                <button class="zoom-btn" data-action="open-image" type="button" title="Open full screen"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg></button>
                <span class="screenshot-zoom-label" title="Hold Ctrl/Cmd and scroll to zoom">${zoomPercent}%</span>
                <button class="zoom-btn" data-action="zoom-out" type="button" ${this.screenshotZoom <= IssueDrawer.SCREENSHOT_ZOOM_MIN ? 'disabled' : ''} aria-label="Zoom out"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                <button class="zoom-btn" data-action="zoom-in" type="button" ${this.screenshotZoom >= IssueDrawer.SCREENSHOT_ZOOM_MAX ? 'disabled' : ''} aria-label="Zoom in"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                <button class="zoom-btn" data-action="zoom-reset" type="button" ${this.screenshotZoom === IssueDrawer.SCREENSHOT_ZOOM_MIN ? 'disabled' : ''} aria-label="Reset zoom" title="Reset">1×</button>
              </div>
            </div>
            <div class="screenshot-viewport${this.screenshotZoom > IssueDrawer.SCREENSHOT_ZOOM_MIN ? ' zoomed' : ''}">
              <img src="${esc(screenshot)}" alt="Screenshot" style="transform: scale(${this.screenshotZoom}); transform-origin: top center;" />
            </div>
          </div>
          ${this.screenshotExpanded ? `
            <div class="screenshot-modal" data-action="close-image-backdrop">
              <div class="screenshot-modal-card">
                <div class="screenshot-toolbar screenshot-modal-toolbar">
                  <span class="field-label">Captured image</span>
                  <div class="screenshot-controls">
                    <span class="screenshot-zoom-label">${zoomPercent}%</span>
                    <button class="zoom-btn" data-action="zoom-out" type="button" ${this.screenshotZoom <= IssueDrawer.SCREENSHOT_ZOOM_MIN ? 'disabled' : ''} aria-label="Zoom out"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                    <button class="zoom-btn" data-action="zoom-in" type="button" ${this.screenshotZoom >= IssueDrawer.SCREENSHOT_ZOOM_MAX ? 'disabled' : ''} aria-label="Zoom in"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>
                    <button class="zoom-btn" data-action="zoom-reset" type="button" ${this.screenshotZoom === IssueDrawer.SCREENSHOT_ZOOM_MIN ? 'disabled' : ''} aria-label="Reset zoom" title="Reset">1×</button>
                    <button class="zoom-btn" data-action="close-image" type="button" aria-label="Close image"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="8 3 2 3 2 9"/><polyline points="16 21 22 21 22 15"/><line x1="2" y1="3" x2="9" y2="10"/><line x1="22" y1="21" x2="15" y2="14"/></svg></button>
                  </div>
                </div>
                <div class="screenshot-modal-viewport">
                  <img src="${esc(screenshot)}" alt="Screenshot" style="transform: scale(${this.screenshotZoom}); transform-origin: top center;" />
                </div>
              </div>
            </div>
          ` : ''}
        ` : detailLoading ? '<div class="screenshot-preview screenshot-preview-loading"></div>' : '<div class="empty">Image not available.</div>'}
        <div class="detail-actions">
          <label class="field-block">
            <span class="field-label">Add comment</span>
            <textarea class="detail-input" data-field="new-comment" rows="3" placeholder="Add a flat item comment"></textarea>
          </label>
          <div class="detail-button-row"><button class="btn-secondary" data-action="add-comment"><span class="btn-label">Add comment</span><span class="btn-spinner" aria-hidden="true"></span></button></div>
          <label class="field-block">
            <span class="field-label">Resolution note</span>
            <textarea class="detail-input" data-field="resolution" rows="3" placeholder="${esc(hasFullDetails ? 'Required when resolving this item' : 'Not available')}">${esc(item.resolution ?? '')}</textarea>
          </label>
          <div class="detail-button-row"><button class="btn-secondary" data-action="resolve"><span class="btn-label">Resolve item</span><span class="btn-spinner" aria-hidden="true"></span></button></div>
        </div>
        <div class="annotation-detail-list">${comments}</div>
        </div>
        ${detailLoading ? `<div class="detail-loading-overlay">${this.loadingHtml('Loading item details…')}</div>` : ''}
      </section>
    `
  }
}
